import { showAlert } from "@components/alert/action.js";
import { useVerification } from "@layouts/v4/verificationPopup/index.js";
import moment from "moment";
import React from "react";
import { connect } from "react-redux";
import { adjustInbox, getInbox, reduceNotificationCounter } from "../../actions/profile.js";
import { openNotification } from "../../actions/websocket.js";
import Loading from "../../components/loading/index.jsx";
import NoContent from "../profile-v2/pages/history-v2/components/NoContent/index.js";
import ArrowDown from "./assets/images/arrow-down.svg";
import MailIcon from "./assets/images/mail.svg";
import "./assets/styles/bonuses.sass";
import "./assets/styles/inbox.sass";
import SubPage from "./subPage.jsx";

const PAGE_SIZE = 50;
const IS_NEW_KEY = "isNewNotifications";
const NOT_NEW_KEY = "notNewNotifications";

export class Inbox extends SubPage {
	getPageHeader(t) {
		return t("INBOX");
	}

	constructor(props) {
		super(props);
		const search = props.location.search;
		const params = new URLSearchParams(search);
		const messageId = params.get("messageId");
		let activeItemIndex = -1;
		if (messageId && props.inbox.notifications.length > 0) {
			activeItemIndex = props.inbox.notifications.findIndex(
				(item) => item.submissionTime === messageId
			);
		}

		let notifications = this.props.inbox.notifications;
		const isNewNotifications = new Set(JSON.parse(localStorage.getItem(IS_NEW_KEY)) || []);
		const notNewNotifications = new Set(JSON.parse(localStorage.getItem(NOT_NEW_KEY)) || []);

		notifications.forEach((item) => {
			if (isNewNotifications.has(item.submissionTime)) {
				item.isNew = "true";
			} else if (notNewNotifications.has(item.submissionTime)) {
				item.isNew = "false";
			} else {
				item.isNew = "true";
				isNewNotifications.add(item.submissionTime);
			}
		});

		localStorage.setItem(IS_NEW_KEY, JSON.stringify(Array.from(isNewNotifications)));
		localStorage.setItem(NOT_NEW_KEY, JSON.stringify(Array.from(notNewNotifications)));

		this.state = {
			prevY: 0,
			activeItemIndex: activeItemIndex,
			allRead: this.checkAllRead(props.inbox.notifications),
		};
		this.openInitialNotification();
	}

	componentDidMount() {
		this.setState({ ...this.state, showHeader: false, showSidebar: false });
		this.props.getInbox({ size: PAGE_SIZE }, () => {
			if (this.loading) {
				this.observer = new IntersectionObserver(this.handlePagination, {
					root: null,
					rootMargin: "0px",
					threshold: 1.0,
				});
				this.observer.observe(this.loading);
			}
			this.loadNotificationsFromLocalStorage();
		});
	}

	componentDidUpdate(prevProps) {
		const { inbox } = this.props;

		if (this.verificationLinkListeners) {
			this.verificationLinkListeners.forEach(({ link, handler }) => {
				link.removeEventListener("click", handler);
			});
		}

		const allVerificationLinks = document.querySelectorAll(".verification-link");

		this.verificationLinkListeners = Array.from(allVerificationLinks).map((link) => {
			link.addEventListener("click", this.manualVerificationRequest);
			return { link, handler: this.manualVerificationRequest };
		});

		if (prevProps.inbox.notifications.length !== inbox.notifications.length) {
			const uniqueNotifications = [
				...new Map(inbox.notifications.map((item) => [item.submissionTime, item])).values(),
			];
			this.openInitialNotification();
			this.loadNotificationsFromLocalStorage();
			this.props.adjustInbox(uniqueNotifications);
		}
	}

	componentWillUnmount() {
		if (this.verificationLinkListeners) {
			this.verificationLinkListeners.forEach(({ link, handler }) => {
				link.removeEventListener("click", handler);
			});
			this.verificationLinkListeners = null;
		}
	}

	manualVerificationRequest = async (event) => {
		event.preventDefault();
		const { withVerification } = this.props;
		const username = JSON.parse(localStorage.getItem("user_data"))?.username;

		if (withVerification && username) {
			try {
				await withVerification(() =>
					Promise.resolve({ username, manuallink: event.target.href })
				);

				this.props.showAlert({
					type: "success",
					title: "SUCCESS",
					msg: "VERIFICATION_LINK_VERIFIED",
				});
			} catch (error) {
				if (error.message.includes("verification.closed")) {
					return;
				}

				this.props.showAlert({
					type: "error",
					title: "ERROR",
					msg: error.message,
				});
			}
		} else {
			this.props.showAlert({
				type: "error",
				title: "ERROR",
				msg: "VERIFICATION_LINK_BROKEN",
			});
		}
	};

	loadNotificationsFromLocalStorage = () => {
		let notifications = this.props.inbox.notifications;
		const isNewNotifications = new Set(JSON.parse(localStorage.getItem(IS_NEW_KEY)) || []);
		const notNewNotifications = new Set(JSON.parse(localStorage.getItem(NOT_NEW_KEY)) || []);

		notifications.forEach((item) => {
			if (isNewNotifications.has(item.submissionTime)) {
				item.isNew = "true";
			} else if (notNewNotifications.has(item.submissionTime)) {
				item.isNew = "false";
			} else {
				item.isNew = "true";
				isNewNotifications.add(item.submissionTime);
			}
		});

		localStorage.setItem(IS_NEW_KEY, JSON.stringify(Array.from(isNewNotifications)));
		localStorage.setItem(NOT_NEW_KEY, JSON.stringify(Array.from(notNewNotifications)));
		this.props.adjustInbox(notifications);
		let allRead = this.checkAllRead(notifications);
		this.setState({ allRead });
	};

	checkAllRead = (notifications) => {
		return notifications.every((item) => item.isNew === "false");
	};

	openInitialNotification = () => {
		const search = this.props.location.search;
		const params = new URLSearchParams(search);
		const messageId = params.get("messageId");
		const isNewNotifications = JSON.parse(localStorage.getItem(IS_NEW_KEY));
		if (messageId && isNewNotifications && isNewNotifications.length > 0) {
			const index = isNewNotifications.findIndex((item) => item === messageId);
			if (index > -1) {
				this.toggleNotification(this.props.inbox.notifications[index], index);
			}
		}
	};

	toggleNotification = async (item, index) => {
		let currentIndex = this.state.activeItemIndex;
		const isNewNotifications = JSON.parse(localStorage.getItem(IS_NEW_KEY));

		if (isNewNotifications.length > 0 && isNewNotifications.includes(item.submissionTime)) {
			let itemRead = item;
			await Promise.all([
				this.markAsReadOrUnread({ stopPropagation: () => {} }, itemRead, index),
				this.props.openNotification(item.tenant, item.username, item.time),
			]);
		}

		this.setState({ activeItemIndex: index !== currentIndex ? index : -1 });
	};

	markAsReadOrUnread = (event, item, index) => {
		event.stopPropagation();
		let notifications = this.props.inbox.notifications.slice();
		let itemToModify = notifications[index];
		let submissionTime = itemToModify.submissionTime;

		itemToModify.isNew = itemToModify.isNew === "true" ? "false" : "true";
		notifications[index] = itemToModify;

		this.props.adjustInbox(notifications);

		let isNewNotifications = new Set(JSON.parse(localStorage.getItem(IS_NEW_KEY)) || []);
		let notNewNotifications = new Set(JSON.parse(localStorage.getItem(NOT_NEW_KEY)) || []);

		isNewNotifications.delete(submissionTime);
		notNewNotifications.delete(submissionTime);

		if (itemToModify.isNew === "true") {
			isNewNotifications.add(submissionTime);
		} else {
			notNewNotifications.add(submissionTime);
		}

		localStorage.setItem(IS_NEW_KEY, JSON.stringify(Array.from(isNewNotifications)));
		localStorage.setItem(NOT_NEW_KEY, JSON.stringify(Array.from(notNewNotifications)));
	};

	markAllAsRead = () => {
		let notifications = this.props.inbox.notifications;
		let notNewNotifications = new Set(JSON.parse(localStorage.getItem(NOT_NEW_KEY)) || []);

		notifications.forEach((item) => {
			item.isNew = "false";
			if (item.submissionTime) {
				notNewNotifications.add(item.submissionTime);
			}
		});

		this.props.adjustInbox(notifications, () => {
			this.props.reduceNotificationCounter();
			this.updateLocalStorage(notifications);
		});

		localStorage.setItem(NOT_NEW_KEY, JSON.stringify(Array.from(notNewNotifications)));
		localStorage.setItem(IS_NEW_KEY, JSON.stringify([]));

		this.setState({ allRead: true });
	};

	handlePagination = (entities, observer) => {
		const y = entities[0].boundingClientRect.y;
		if (this.state.prevY > y && this.props.inbox.offset && !this.props.loading) {
			this.props.getInbox({ size: PAGE_SIZE, offset: this.props.inbox.offset });
		} else if (!this.state.prevY) {
			this.props.getInbox({ size: PAGE_SIZE });
		}

		if (y > this.state.prevY) {
			this.setState({ prevY: y });
		}
	};

	updateLocalStorage = (itemRead) => {
		let isNewNotifications = new Set(JSON.parse(localStorage.getItem(IS_NEW_KEY)) || []);
		let notNewNotifications = new Set(JSON.parse(localStorage.getItem(NOT_NEW_KEY)) || []);

		if (itemRead.isNew === "false" && itemRead.submissionTime) {
			isNewNotifications.delete(itemRead.submissionTime);
			notNewNotifications.add(itemRead.submissionTime);
		} else if (itemRead.submissionTime) {
			notNewNotifications.delete(itemRead.submissionTime);
			isNewNotifications.add(itemRead.submissionTime);
		}

		localStorage.setItem(IS_NEW_KEY, JSON.stringify(Array.from(isNewNotifications)));
		localStorage.setItem(NOT_NEW_KEY, JSON.stringify(Array.from(notNewNotifications)));
	};

	getPageContent(t) {
		const layout = {
				wrapperCol: { span: 70 },
			},
			{ loading, inbox } = this.props,
			{ activeItemIndex } = this.state;
		let isNewNotifications = JSON.parse(localStorage.getItem(IS_NEW_KEY)) || [];

		return (
			<div
				className={`cube-form-wrapper inbox-container ${
					!this.state.allRead ? "hasButton" : ""
				}`}>
				<Loading loading={loading} />
				{inbox?.notifications && inbox.notifications.length > 0 ? (
					<div className="notification-list">
						{inbox.notifications.map((item, index) => (
							<div
								className={`notification-item ${
									activeItemIndex === index ? "active" : ""
								} ${item.isNew === "false" ? "read" : ""}`}
								key={`notification-key-${index}`}
								onClick={this.toggleNotification.bind(this, item, index)}>
								<div className="item-wrapper">
									<div className="date cell">
										<span>
											{moment(
												parseInt(
													item?.time ? item?.time : item?.submissionTime
												)
											).format("DD/MM/YYYY HH:mm")}
										</span>
										{item?.isNew === "true" ? (
											<span className="new-text">{t("New")}</span>
										) : null}
									</div>
									<div className={"notification-row"}>
										<div className="subject cell">
											<span className="subject-text">{t(item.subject)}</span>
										</div>
									</div>
									<div className="message-row">
										<div
											className="message"
											dangerouslySetInnerHTML={{
												__html: item.message,
											}}
										/>
									</div>
									<div className="button-wrapper">
										<div
											className={`expandButton ${
												activeItemIndex === index ? "up" : ""
											}`}>
											{activeItemIndex === index
												? t("READ LESS")
												: t("READ MORE")}
											<img
												src={ArrowDown}
												alt="Arrow Icon"
											/>
										</div>
										<div className="markButton">
											<div
												className="mark-as-read"
												onClick={(event) =>
													this.markAsReadOrUnread(event, item, index)
												}>
												<img
													src={MailIcon}
													alt="Mail Icon"
												/>
												{item.isNew === "true"
													? t("MARK AS READ")
													: t("MARK AS UNREAD")}
											</div>
										</div>
									</div>
								</div>
							</div>
						))}
						{isNewNotifications.length > 0 ? (
							<>
								<div className="mark-btn-overlay"></div>
								<div
									className="mark-all-btn"
									onClick={this.markAllAsRead}>
									{t("MARK ALL AS READ")}
								</div>
							</>
						) : null}
						<div
							ref={(x) => (this.loading = x)}
							className="notification-loading-ref">
							{loading && <span>{t("LOADING")}</span>}
						</div>
					</div>
				) : (
					<NoContent
						translate={t}
						title={"NoInfoText"}
						description={"NoInfoInbox"}
					/>
				)}
			</div>
		);
	}
}

const mapStateToProps = (state) => ({
	inbox: state.profile.inbox,
	loading: state.profile.inboxLoading,
});

const mapDispatchToProps = {
	getInbox,
	openNotification,
	reduceNotificationCounter,
	adjustInbox,
	showAlert,
};

const WithVerificationWrapper = (Component) => {
	return (props) => {
		const { withVerification } = useVerification();
		return (
			<Component
				{...props}
				withVerification={withVerification}
			/>
		);
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(WithVerificationWrapper(Inbox));
